/* eslint-disable @typescript-eslint/no-explicit-any */
import { Inject, Injectable, Type } from '@angular/core';
import { Router, ActivatedRouteSnapshot, DefaultExport } from '@angular/router';
import { DataService, AppDataSnapshot } from '../data.service';
import { ICrypto, ICryptoToken } from 'src/app/interfaces/encryption-service.interface';
import { firstValueFrom, Observable } from 'rxjs';
import { LoggerService } from '../logger.service';

interface SessionMetadata {
  route: string;
  componentName: string;
  componentDescription: string;
  currentData: AppDataSnapshot;
  order: number;
  timestamp: string;
  requestUrl?: string;
  httpMethod?: string;
}

@Injectable({ providedIn: 'root' })
export class SessionDataLogService {
  private order = 1;

  constructor(
    private router: Router,
    private dataService: DataService,
    @Inject(ICryptoToken) private crypto: ICrypto,
    private logger: LoggerService,
  ) {}

  public async getEncryptedSessionMetadata(requestUrl?: string, httpMethod?: string): Promise<string> {
    try {
      const metadata = await this.buildSessionMetadata(requestUrl, httpMethod);
      return await this.crypto.encrypt(JSON.stringify(metadata));
    } catch (error) {
      this.logger.error('Error generating session metadata:', error);
      return await this.crypto.encrypt(JSON.stringify({}));
    }
  }

  private async buildSessionMetadata(requestUrl?: string, httpMethod?: string): Promise<SessionMetadata> {
    try {
      const routeInfo = await this.getCurrentRouteInfo();
      const snapshot = this.dataService.getAppDataSnapshot();

      return {
        route: routeInfo?.route ?? 'unknown',
        componentName: routeInfo?.componentName ?? 'UnknownComponent',
        componentDescription: routeInfo?.componentDescription ?? 'Unknown component description',
        currentData: snapshot ?? ({} as AppDataSnapshot),
        order: this.order++,
        timestamp: new Date().toISOString(),
        requestUrl: requestUrl ?? 'unknown',
        httpMethod: httpMethod ?? 'unknown',
      };
    } catch (error) {
      this.logger.warn('Error building session metadata, using fallback values:', error);
      return {
        route: 'error',
        componentName: 'ErrorComponent',
        componentDescription: 'Error retrieving component information',
        currentData: {} as AppDataSnapshot,
        order: this.order++,
        timestamp: new Date().toISOString(),
        requestUrl: requestUrl ?? 'error',
        httpMethod: httpMethod ?? 'error',
      };
    }
  }

  private async getCurrentRouteInfo(): Promise<{ route: string; componentName: string; componentDescription: string }> {
    try {
      const snapshot = this.router?.routerState?.snapshot?.root;
      if (!snapshot) {
        throw new Error('Router snapshot not available');
      }

      const activeRoute = this.getDeepestRoute(snapshot);
      if (!activeRoute) {
        throw new Error('Active route not found');
      }

      const route = this.buildFullPath(activeRoute);
      const component = await this.resolveComponent(activeRoute);
      const unwrapped = await this.unwrapComponent(component);

      return {
        route: route ?? '/unknown',
        componentName: (unwrapped as any)?.componentName ?? unwrapped?.name ?? 'UnknownComponent',
        componentDescription: (unwrapped as any)?.componentDescription ?? 'Unknown component description',
      };
    } catch (error) {
      console.warn('Error getting current route info:', error);
      return {
        route: '/error',
        componentName: 'ErrorComponent',
        componentDescription: 'Error retrieving route information',
      };
    }
  }

  private async unwrapComponent(input: any): Promise<Type<any> | null> {
    // Await if it's a Promise
    if (input instanceof Promise) {
      input = await input;
    }

    // Await if it's an Observable
    if (input?.subscribe) {
      input = await firstValueFrom(input);
    }

    // If it's a default export (ESM module), unwrap it
    if (input?.default) {
      input = input.default;
    }

    // Final check: must be a constructor function
    return typeof input === 'function' ? input : null;
  }

  private getDeepestRoute(snapshot: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
    if (!snapshot) {
      throw new Error('Snapshot is null or undefined');
    }
    while (snapshot.firstChild) {
      snapshot = snapshot.firstChild;
    }
    return snapshot;
  }

  private buildFullPath(route: ActivatedRouteSnapshot): string {
    if (!route) {
      return '/unknown';
    }

    const segments: string[] = [];
    let current: ActivatedRouteSnapshot | null = route;

    while (current) {
      if (current.routeConfig?.path) {
        segments.unshift(current.routeConfig.path);
      }
      current = current.parent;
    }

    return segments.length > 0 ? '/' + segments.join('/') : '/';
  }

  private async resolveComponent(
    route: ActivatedRouteSnapshot,
  ): Promise<
    Type<any> | DefaultExport<Type<unknown>> | Observable<Type<unknown> | DefaultExport<Type<unknown>>> | null
  > {
    if (!route || !route.routeConfig) {
      return null;
    }

    if (route.routeConfig.component) {
      return route.routeConfig.component;
    }

    if (route.routeConfig.loadComponent) {
      try {
        const component = await route.routeConfig.loadComponent();
        return component;
      } catch (e) {
        console.warn('Error loading component:', e);
        return null;
      }
    }

    return null;
  }
}
